/**
 * Platform Admin — 模型管理 (Model Catalog).
 *
 * VISTOERA_CANONICAL_PROVIDER_MODEL_CATALOG_NODE_MODEL_BINDING_USAGE_
 * BILLING_FOUNDATION_P0_V1 (task spec §7) — canonical, searchable/
 * filterable/sortable/batch-editable ProviderModel catalog. Replaces the
 * previously-disabled "模型管理" nav placeholder.
 *
 * Discovery (a Provider's raw `GET /models` result) is deliberately a
 * separate state from Vistoera-`enabled` (task spec §5) — every freshly
 * synced row lands here as `DISCOVERED`/disabled, and an admin explicitly
 * opts individual rows into the catalog. Enabling a model here does NOT
 * automatically bind it to any Function Node — that's a separate,
 * explicit step on 功能节点管理 → 添加模型 (`ProvidersPage.tsx`).
 */
import { CheckOutlined, CloudSyncOutlined, ReloadOutlined, StopOutlined } from '@ant-design/icons'
import { Button, Input, InputNumber, Select, Table, Tag, message } from 'antd'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  platformBatchSetProviderModelEnabled,
  platformListCredentialProviders,
  platformListProviderModelFacets,
  platformListProviderModels,
  platformSetProviderModelEnabled,
  platformSetProviderModelSortOrder,
  platformSyncKlingModels,
  platformSyncVolcengineArkModels,
} from '../api'
import type { ModelCatalogStatus, ProviderDefinitionRow, ProviderModelRow } from '../types'
import { SectionCard, formatDateTime } from './adminShared'
import { platformColors } from './theme'

function errMsg(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: { code?: string; detail?: { code?: string; message?: string } } } })
    ?.response?.data
  const code = data?.code || data?.detail?.code
  const detailMessage = data?.detail?.message
  const raw = (err as { message?: string })?.message
  return code ? `${detailMessage || raw || fallback} (${code})` : detailMessage || raw || fallback
}

const STATUS_COLOR: Record<ModelCatalogStatus, string> = {
  DISCOVERED: 'default',
  ENABLED: 'success',
  DISABLED: 'default',
  DEPRECATED: 'warning',
  UNAVAILABLE: 'error',
}

const STATUS_LABEL: Record<ModelCatalogStatus, string> = {
  DISCOVERED: '待启用',
  ENABLED: '已启用',
  DISABLED: '已停用',
  DEPRECATED: '已弃用',
  UNAVAILABLE: 'Provider 已下架',
}

export default function ModelsPage() {
  const [rows, setRows] = useState<ProviderModelRow[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [providers, setProviders] = useState<ProviderDefinitionRow[]>([])
  const [modelTypes, setModelTypes] = useState<string[]>([])
  const [families, setFamilies] = useState<string[]>([])

  const [search, setSearch] = useState('')
  const [providerId, setProviderId] = useState<string | undefined>(undefined)
  const [modelType, setModelType] = useState<string | undefined>(undefined)
  const [enabledFilter, setEnabledFilter] = useState<boolean | undefined>(undefined)
  const [family, setFamily] = useState<string | undefined>(undefined)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [page, setPage] = useState(1)
  const pageSize = 50

  const load = useCallback(() => {
    setLoading(true)
    platformListProviderModels({
      search: search.trim() || undefined,
      provider_id: providerId,
      model_type: modelType,
      enabled: enabledFilter,
      family,
      limit: pageSize,
      offset: (page - 1) * pageSize,
    })
      .then((res) => {
        setRows(res.data.items ?? [])
        setTotal(res.data.total ?? 0)
      })
      .catch((err) => message.error(errMsg(err, '加载模型列表失败')))
      .finally(() => setLoading(false))
  }, [search, providerId, modelType, enabledFilter, family, page])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    platformListCredentialProviders()
      .then((res) => setProviders(res.data.providers ?? []))
      .catch(() => undefined)
    platformListProviderModelFacets()
      .then((res) => {
        setModelTypes(res.data.model_types ?? [])
        setFamilies(res.data.families ?? [])
      })
      .catch(() => undefined)
  }, [])

  // VistoeraTASK-00176 — STATIC (Kling's own verified manifest, no live
  // "list models" endpoint) is a real discovery mode alongside API
  // (Volcengine Ark's live HTTP discovery) — both render a per-provider
  // sync button below; MANUAL providers have no sync action at all.
  const apiDiscoveryProviders = useMemo(
    () => providers.filter((p) => p.model_discovery_mode === 'API' || p.model_discovery_mode === 'STATIC'),
    [providers],
  )

  const providerLabel = useMemo(() => {
    const map = new Map<string, string>()
    providers.forEach((p) => map.set(p.id, p.display_name))
    return map
  }, [providers])

  const syncVolcengineArk = async () => {
    setSyncing(true)
    try {
      const res = await platformSyncVolcengineArkModels()
      const { discovered_count, created_count, updated_count, unavailable_count } = res.data
      message.success(
        `同步完成：发现 ${discovered_count} 个模型（新增 ${created_count}，更新 ${updated_count}，下架 ${unavailable_count}）`,
      )
      setPage(1)
      load()
    } catch (err) {
      message.error(errMsg(err, '同步 Volcengine Ark 模型失败'))
    } finally {
      setSyncing(false)
    }
  }

  // VistoeraTASK-00176 — Kling AI（可灵）STATIC-manifest sync. One click
  // upserts the ProviderModel catalog AND wires NodeModelBinding + the
  // Kling ProviderOption (see backend route docstring) — never a second,
  // Kling-only sync protocol.
  const syncKling = async () => {
    setSyncing(true)
    try {
      const res = await platformSyncKlingModels()
      const { discovered_count, created_count, updated_count, unavailable_count, node_model_binding_created } =
        res.data
      message.success(
        `同步完成：发现 ${discovered_count} 个模型（新增 ${created_count}，更新 ${updated_count}，下架 ${unavailable_count}，新绑定 ${node_model_binding_created} 个功能节点模型）`,
      )
      setPage(1)
      load()
    } catch (err) {
      message.error(errMsg(err, '同步 Kling AI 模型失败'))
    } finally {
      setSyncing(false)
    }
  }

  const toggleEnabled = async (row: ProviderModelRow) => {
    try {
      await platformSetProviderModelEnabled(row.id, !row.enabled)
      message.success(row.enabled ? '已停用' : '已启用')
      load()
    } catch (err) {
      message.error(errMsg(err, '更新模型状态失败'))
    }
  }

  const batchToggle = async (enabled: boolean) => {
    if (selectedIds.length === 0) return
    try {
      await platformBatchSetProviderModelEnabled(selectedIds, enabled)
      message.success(`已${enabled ? '启用' : '停用'} ${selectedIds.length} 个模型`)
      setSelectedIds([])
      load()
    } catch (err) {
      message.error(errMsg(err, '批量更新失败'))
    }
  }

  const updateSortOrder = async (row: ProviderModelRow, value: number | null) => {
    if (value === null) return
    try {
      await platformSetProviderModelSortOrder(row.id, value)
      load()
    } catch (err) {
      message.error(errMsg(err, '更新排序失败'))
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: platformColors.primaryText }}>模型管理</h1>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: platformColors.secondaryText, maxWidth: 760 }}>
            Provider 发现（Discovery）的模型不等于 Vistoera 已启用的模型 —— 在此搜索、启用/停用、排序具体模型后，再到
            <a href="/platform/providers"> 功能节点管理 → 添加模型 </a>
            为对应功能节点绑定。同步操作只读取模型列表，不会产生任何计费生成。
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {apiDiscoveryProviders.map((p) => {
            if (p.provider_key === 'volcengine_ark') {
              return (
                <Button
                  key={p.id}
                  icon={<CloudSyncOutlined />}
                  loading={syncing}
                  onClick={() => void syncVolcengineArk()}
                  data-testid="sync-volcengine-ark"
                >
                  同步 {p.display_name} 模型
                </Button>
              )
            }
            if (p.provider_key === 'kling') {
              return (
                <Button
                  key={p.id}
                  icon={<CloudSyncOutlined />}
                  loading={syncing}
                  onClick={() => void syncKling()}
                  data-testid="sync-kling"
                >
                  同步 {p.display_name} 模型
                </Button>
              )
            }
            return null
          })}
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
            刷新
          </Button>
        </div>
      </div>

      <SectionCard title="Model Catalog">
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <Input.Search
            placeholder="搜索模型名称 / family / 外部 model id（例如 Seedance）"
            allowClear
            style={{ width: 320 }}
            onSearch={(v) => {
              setSearch(v)
              setPage(1)
            }}
            data-testid="model-search-input"
          />
          <Select
            allowClear
            placeholder="Provider"
            style={{ width: 180 }}
            value={providerId}
            onChange={(v) => {
              setProviderId(v)
              setPage(1)
            }}
            options={providers.map((p) => ({ value: p.id, label: p.display_name }))}
          />
          <Select
            allowClear
            placeholder="模型类型"
            style={{ width: 160 }}
            value={modelType}
            onChange={(v) => {
              setModelType(v)
              setPage(1)
            }}
            options={modelTypes.map((t) => ({ value: t, label: t }))}
          />
          <Select
            allowClear
            placeholder="Family"
            style={{ width: 140 }}
            value={family}
            onChange={(v) => {
              setFamily(v)
              setPage(1)
            }}
            options={families.map((f) => ({ value: f, label: f }))}
          />
          <Select
            allowClear
            placeholder="启用状态"
            style={{ width: 130 }}
            value={enabledFilter === undefined ? undefined : String(enabledFilter)}
            onChange={(v) => {
              setEnabledFilter(v === undefined ? undefined : v === 'true')
              setPage(1)
            }}
            options={[
              { value: 'true', label: '已启用' },
              { value: 'false', label: '未启用' },
            ]}
          />
          {selectedIds.length > 0 && (
            <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
              <span style={{ color: platformColors.mutedText, fontSize: 13, alignSelf: 'center' }}>
                已选 {selectedIds.length} 个
              </span>
              <Button size="small" icon={<CheckOutlined />} onClick={() => void batchToggle(true)}>
                批量启用
              </Button>
              <Button size="small" icon={<StopOutlined />} onClick={() => void batchToggle(false)}>
                批量停用
              </Button>
            </div>
          )}
        </div>

        <Table
          rowKey="id"
          loading={loading}
          dataSource={rows}
          size="middle"
          scroll={{ x: 1100 }}
          rowSelection={{
            selectedRowKeys: selectedIds,
            onChange: (keys) => setSelectedIds(keys as string[]),
          }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: false,
            onChange: (p) => setPage(p),
          }}
          columns={[
            {
              title: '模型',
              dataIndex: 'display_name',
              render: (name: string, row) => (
                <div>
                  <div style={{ fontWeight: 600 }}>{name}</div>
                  <div style={{ fontSize: 11, color: platformColors.mutedText }}>{row.provider_model_id}</div>
                </div>
              ),
            },
            {
              title: 'Provider',
              dataIndex: 'provider_id',
              width: 160,
              render: (id: string, row) => row.provider_display_name || providerLabel.get(id) || id,
            },
            {
              title: 'Family',
              dataIndex: 'family',
              width: 110,
              render: (v: string | null) => v || <span style={{ color: platformColors.mutedText }}>—</span>,
            },
            {
              title: '类型',
              dataIndex: 'model_type',
              width: 130,
              render: (v: string) => <Tag>{v}</Tag>,
            },
            {
              title: '状态',
              dataIndex: 'catalog_status',
              width: 110,
              render: (status: ModelCatalogStatus) => <Tag color={STATUS_COLOR[status]}>{STATUS_LABEL[status]}</Tag>,
            },
            {
              title: '已绑定节点数',
              dataIndex: 'binding_count',
              width: 100,
              render: (n: number) => n,
            },
            {
              title: '排序',
              dataIndex: 'sort_order',
              width: 90,
              render: (v: number, row) => (
                <InputNumber
                  size="small"
                  defaultValue={v}
                  style={{ width: 70 }}
                  onPressEnter={(e) => void updateSortOrder(row, Number((e.target as HTMLInputElement).value))}
                  onBlur={(e) => void updateSortOrder(row, Number(e.target.value))}
                />
              ),
            },
            {
              title: '最近同步',
              dataIndex: 'last_synced_at',
              width: 150,
              render: (v: string | null) => formatDateTime(v),
            },
            {
              title: '操作',
              key: 'actions',
              width: 100,
              fixed: 'right',
              render: (_: unknown, row) => (
                <Button size="small" onClick={() => void toggleEnabled(row)}>
                  {row.enabled ? '停用' : '启用'}
                </Button>
              ),
            },
          ]}
        />
      </SectionCard>
    </div>
  )
}
